import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { useBossAuth } from "@/lib/contexts";

export default function BossLoginPage() {
  const { login } = useBossAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("boss@tablezy.com");
  const [password, setPassword] = useState("boss123");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      return toast.error("Please enter email and password");
    }

    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Boss Authentication Successful");
      navigate("/boss/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication Failed. Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-body flex flex-col justify-center items-center p-4 relative overflow-hidden">
      {/* Glows */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] bg-primary/20 rounded-full blur-[140px] pointer-events-none" />

      <div className="w-full max-w-md space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-3xl bg-primary text-white grid place-items-center mx-auto shadow-xl shadow-primary/30">
            <QrCode className="w-9 h-9 stroke-[2.2]" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Tablezy Boss Panel</h1>
          <p className="text-slate-400 text-xs font-mono">CONFIDENTIAL & AUTHORIZED PERSONNEL ONLY</p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-8 shadow-2xl space-y-6 backdrop-blur-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Boss Account Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  required
                  placeholder="boss@tablezy.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 pl-11 pr-4 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Secure Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-11 pr-4 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50 shadow-lg shadow-primary/25"
            >
              <span>{loading ? "Authenticating Boss..." : "Sign In To Boss Panel"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="pt-4 border-t border-slate-800 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Protected Route & Role Authorization</span>
          </div>
        </div>

        <div className="text-center text-xs text-slate-600 font-mono">
          Tablezy Boss Control Center © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
